import { stripParamSlotSuffixes } from './param-slot-classifier';

export type WebSocketMessageSample = {
  direction: 'send' | 'receive';
  opcode:
    | 'text'
    | 'binary'
    | 'continuation'
    | 'ping'
    | 'pong'
    | 'close'
    | string;
  time?: number;
  data: string;
  truncated?: boolean;
};

const WS_SCHEME_REGEX = /^wss?:\/\//i;
const HTTP_SCHEME_REGEX = /^https?:\/\//i;

export function isWebSocketUrl(url: string): boolean {
  return WS_SCHEME_REGEX.test(url.trim());
}

export function httpUrlToWebSocketUrl(url: string): string {
  const trimmed = url.trim();

  if (isWebSocketUrl(trimmed)) {
    return trimmed;
  }

  if (/^https:\/\//i.test(trimmed)) {
    return trimmed.replace(/^https:/i, 'wss:');
  }

  if (/^http:\/\//i.test(trimmed)) {
    return trimmed.replace(/^http:/i, 'ws:');
  }

  return trimmed;
}

export function isWebSocketHandshakeResponse({
  status,
  headers,
}: {
  status?: number | null;
  headers?: ReadonlyArray<{ name?: string | null; value?: string | null }>;
}): boolean {
  if (status !== 101) {
    return false;
  }

  const upgrade = headers?.find(
    (header) => header.name?.toLowerCase() === 'upgrade',
  )?.value;

  return upgrade?.toLowerCase().includes('websocket') ?? false;
}

export function isWebSocketBatchPartition({
  url,
  status,
  upgradeHeader,
  headers,
}: {
  url?: string | null;
  status?: number | null;
  upgradeHeader?: string | null;
  headers?: ReadonlyArray<{ name?: string | null; value?: string | null }>;
}): boolean {
  if (url && isWebSocketUrl(url)) {
    return true;
  }

  return isWebSocketHandshakeResponse({
    status,
    headers:
      headers ??
      (upgradeHeader ? [{ name: 'upgrade', value: upgradeHeader }] : undefined),
  });
}

export function isWebSocketHarTrack({
  url,
  status,
  headers,
  webSocketMessages,
  isClosed,
}: {
  url?: string | null;
  status?: number | null;
  headers?: ReadonlyArray<{ name?: string | null; value?: string | null }>;
  webSocketMessages?: unknown;
  isClosed?: boolean;
}): boolean {
  if (url && isWebSocketUrl(url)) {
    return true;
  }

  if (Array.isArray(webSocketMessages) && webSocketMessages.length > 0) {
    return true;
  }

  if (isClosed === true) {
    return true;
  }

  return isWebSocketHandshakeResponse({ status, headers });
}

export function extractWebSocketSubprotocol({
  requestHeaders,
  responseHeaders,
}: {
  requestHeaders?: ReadonlyArray<{
    name?: string | null;
    value?: string | null;
  }>;
  responseHeaders?: ReadonlyArray<{
    name?: string | null;
    value?: string | null;
  }>;
}): string | null {
  const fromResponse = responseHeaders?.find(
    (header) => header.name?.toLowerCase() === 'sec-websocket-protocol',
  )?.value;

  if (fromResponse?.trim()) {
    return fromResponse.trim();
  }

  const fromRequest = requestHeaders?.find(
    (header) => header.name?.toLowerCase() === 'sec-websocket-protocol',
  )?.value;

  if (!fromRequest?.trim()) {
    return null;
  }

  return fromRequest.split(',')[0]?.trim() || null;
}

export function isHttpOrWebSocketUrl(url: string): boolean {
  const trimmed = url.trim();

  return HTTP_SCHEME_REGEX.test(trimmed) || WS_SCHEME_REGEX.test(trimmed);
}

export const WEBSOCKET_JSON_DISCRIMINATORS = [
  'type',
  'event',
  'method',
  'op',
  'action',
] as const;

export function matchesWebSocketCodeGraphReuseIdentity({
  left,
  right,
}: {
  left: {
    host?: string | null;
    port?: number | null;
    subprotocol?: string | null;
  };
  right: {
    host?: string | null;
    port?: number | null;
    subprotocol?: string | null;
  };
}): boolean {
  const leftHost = left.host?.trim().toLowerCase() ?? '';
  const rightHost = right.host?.trim().toLowerCase() ?? '';
  const leftSubprotocol = left.subprotocol?.trim() ?? null;
  const rightSubprotocol = right.subprotocol?.trim() ?? null;

  return (
    leftHost === rightHost &&
    (left.port ?? null) === (right.port ?? null) &&
    leftSubprotocol === rightSubprotocol
  );
}

export function parseWebSocketMessageSamples(
  raw: unknown,
): WebSocketMessageSample[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return [];
    }

    const record = item as Record<string, unknown>;
    const direction =
      record.direction === 'send' || record.direction === 'receive'
        ? record.direction
        : null;

    if (!direction || typeof record.data !== 'string') {
      return [];
    }

    return [
      {
        direction,
        opcode: typeof record.opcode === 'string' ? record.opcode : 'text',
        time: typeof record.time === 'number' ? record.time : undefined,
        data: record.data,
        truncated:
          typeof record.truncated === 'boolean' ? record.truncated : undefined,
      },
    ];
  });
}

export const DEFAULT_WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES = 50;
export const WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES_CEILING = 500;
export const DEFAULT_WEBSOCKET_HAR_MAX_PAYLOAD_CHARS = 4_096;
export const DEFAULT_WEBSOCKET_HAR_LAST_FRAME_COUNT = 10;

export function resolveWebSocketHarMaxMessageSamples(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return DEFAULT_WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES;
  }

  return Math.min(
    WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES_CEILING,
    Math.max(1, Math.floor(value)),
  );
}

export function capWebSocketMessages({
  messages,
  maxMessages = DEFAULT_WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES,
  maxPayloadChars = DEFAULT_WEBSOCKET_HAR_MAX_PAYLOAD_CHARS,
}: {
  messages: readonly WebSocketMessageSample[];
  maxMessages?: number;
  maxPayloadChars?: number;
}): WebSocketMessageSample[] {
  const truncated = messages
    .filter((message) => message.opcode !== 'ping' && message.opcode !== 'pong')
    .map((message) =>
      truncateWebSocketMessagePayload(message, maxPayloadChars),
    );

  if (truncated.length <= maxMessages) {
    return truncated;
  }

  const lastFrameCount = resolveWebSocketHarLastFrameCount(maxMessages);
  const lastFrames = lastFrameCount > 0 ? truncated.slice(-lastFrameCount) : [];
  const lastFrameSet = new Set(lastFrames);
  const remaining = truncated.filter((message) => !lastFrameSet.has(message));
  const shapeBudget = maxMessages - lastFrames.length;

  if (shapeBudget <= 0) {
    return lastFrames;
  }

  return [
    ...selectShapeCappedWebSocketSamples({
      messages: remaining,
      maxMessages: shapeBudget,
    }),
    ...lastFrames,
  ];
}

function resolveWebSocketHarLastFrameCount(maxMessages: number): number {
  return Math.min(
    DEFAULT_WEBSOCKET_HAR_LAST_FRAME_COUNT,
    Math.floor(maxMessages / 2),
  );
}

function selectShapeCappedWebSocketSamples({
  messages,
  maxMessages,
}: {
  messages: WebSocketMessageSample[];
  maxMessages: number;
}): WebSocketMessageSample[] {
  if (messages.length <= maxMessages) {
    return messages;
  }

  const lastByShape = new Map<string, WebSocketMessageSample>();

  for (const message of messages) {
    lastByShape.set(websocketMessageShapeKey(message), message);
  }

  const uniqueByFirstSeen = [...lastByShape.values()];

  if (uniqueByFirstSeen.length <= maxMessages) {
    return fillRemainingWebSocketSamples({
      selected: uniqueByFirstSeen,
      candidates: messages,
      maxMessages,
    });
  }

  const headCount = Math.ceil(maxMessages / 2);
  const tailCount = maxMessages - headCount;
  const selectedByShape = new Map<string, WebSocketMessageSample>();

  for (const message of uniqueByFirstSeen.slice(0, headCount)) {
    selectedByShape.set(websocketMessageShapeKey(message), message);
  }

  if (tailCount > 0) {
    for (const message of uniqueByFirstSeen.slice(-tailCount)) {
      selectedByShape.set(websocketMessageShapeKey(message), message);
    }
  }

  return [...selectedByShape.values()].slice(0, maxMessages);
}

function truncateWebSocketMessagePayload(
  message: WebSocketMessageSample,
  maxPayloadChars: number,
): WebSocketMessageSample {
  if (message.data.length <= maxPayloadChars) {
    return { ...message, truncated: message.truncated ?? false };
  }

  return {
    ...message,
    data: message.data.slice(0, maxPayloadChars),
    truncated: true,
  };
}

function fillRemainingWebSocketSamples({
  selected,
  candidates,
  maxMessages,
}: {
  selected: WebSocketMessageSample[];
  candidates: WebSocketMessageSample[];
  maxMessages: number;
}): WebSocketMessageSample[] {
  if (selected.length >= maxMessages) {
    return selected.slice(0, maxMessages);
  }

  const alreadyChosen = new Set(selected);
  const extras: WebSocketMessageSample[] = [];

  for (let index = candidates.length - 1; index >= 0; index -= 1) {
    if (selected.length + extras.length >= maxMessages) {
      break;
    }

    const message = candidates[index];

    if (!message || alreadyChosen.has(message)) {
      continue;
    }

    extras.push(message);
    alreadyChosen.add(message);
  }

  return [...selected, ...extras];
}

function websocketMessageShapeKey(message: WebSocketMessageSample): string {
  if (message.opcode === 'binary') {
    return `${message.direction}|binary`;
  }

  const trimmed = message.data.trim();

  if (trimmed === '') {
    return `${message.direction}|empty`;
  }

  try {
    return `${message.direction}|json|${jsonValueShapeKey(JSON.parse(trimmed))}`;
  } catch {
    return `${message.direction}|text`;
  }
}

function jsonValueShapeKey(value: unknown): string {
  if (value === null) {
    return 'null';
  }

  if (Array.isArray(value)) {
    return `array:${jsonValueShapeKey(value[0])}`;
  }

  if (typeof value !== 'object') {
    return typeof value;
  }

  const record = value as Record<string, unknown>;
  const discriminators = WEBSOCKET_JSON_DISCRIMINATORS.flatMap((key) => {
    const discriminator = record[key];

    return typeof discriminator === 'string' ? [`${key}=${discriminator}`] : [];
  });
  const keys = Object.keys(record).sort().join(',');

  return `object:${discriminators.join(',')}|${keys}`;
}

export function buildWebSocketEndpointIdentityKey({
  applicationId,
  url,
  subprotocol,
  parameterizedPathOverride,
}: {
  applicationId: string;
  url: string;
  subprotocol?: string | null;
  parameterizedPathOverride?: string | null;
}): string {
  const wsUrl = httpUrlToWebSocketUrl(url);
  let normalizedUrl = wsUrl;

  try {
    const parsed = new URL(wsUrl);
    const defaultPort =
      parsed.protocol === 'wss:'
        ? '443'
        : parsed.protocol === 'ws:'
          ? '80'
          : null;
    const port =
      parsed.port && parsed.port !== defaultPort ? `:${parsed.port}` : '';
    const pathname = resolveWebSocketIdentityPath({
      pathname: parsed.pathname,
      parameterizedPathOverride,
    });

    normalizedUrl = `${parsed.protocol}//${parsed.hostname.toLowerCase()}${port}${pathname}`;
  } catch {
    const withoutQuery = wsUrl.split('?')[0] ?? wsUrl;

    normalizedUrl = withoutQuery.toLowerCase();
  }

  return [
    applicationId,
    'GET',
    normalizedUrl,
    subprotocol?.trim() ?? '',
    'WEBSOCKET',
  ].join('|');
}

export function resolveWebSocketIdentityPath({
  pathname,
  parameterizedPathOverride,
}: {
  pathname: string;
  parameterizedPathOverride?: string | null;
}): string {
  const rawPath = parameterizedPathOverride
    ? parameterizedPathOverride.startsWith('/')
      ? parameterizedPathOverride
      : `/${parameterizedPathOverride}`
    : pathname;

  return stripParamSlotSuffixes(rawPath);
}
